import React, { useState, useEffect, useContext } from 'react'
import { Trans, useTranslation }                  from 'react-i18next'
import { useApi }                           from '../utils/api'
import BaseTable                            from '../components/base-table'
import {
  Box, Button,
  Container,
  Link,
  Stack,
  TextField,
  Typography
}                                        from '@mui/material'
import { Link as RouterLink, useParams } from 'react-router-dom'
import {
  dateFromInputStringToSql, dateToInputString,
  dateToUserString,
}                                        from '../utils/dateutils'
import { fPrice }                        from '../utils/textutils'
import Page                              from '../components/Page'
import { DateTime }                      from 'luxon'
import { MessageDeliveryStatus, MessageProcessingStatus, User } from '../consts'
import { AppContext }                    from '../App'

export default function SearchPage () {

  const api = useApi()
  const { user } = useContext(AppContext)
  const { t } = useTranslation()
  const { telNumber } = useParams()
  const [dateTimeFrom, setDateTimeFrom] = useState(dateToInputString(DateTime.now().minus({ months: 1 }).startOf('day').toISO()))
  const [dateTimeTo, setDateTimeTo] = useState(dateToInputString(DateTime.now().plus({ days: 1 }).startOf('day').toISO()))
  const [forceReloadCounter, setForceReloadCounter] = useState(0)

  const columns = [
    { field: 'id', headerName: t('ID'), width: 70, sortable: false, filterable: false, renderCell: (cell) => (
        <Link component={RouterLink} to={`/dashboard/messages/${cell.row.id}`}>
          {cell.row.id}
        </Link> )
    },
    { field: 'processingStatus', headerName: t('Processing status'), width: 150, sortable: false, filterable: false, renderCell: (cell) => {
        return t(cell.row.processingStatus ?? MessageProcessingStatus.scheduled)
      }},
    { field: 'deliveryStatus', headerName: t('Delivery status'), width: 150, sortable: false, filterable: false, renderCell: (cell) => {
        return cell.row.deliveryStatus ? t(cell.row.deliveryStatus) : '---'
      }},
    { field: 'statusChanged', headerName: t('Status changed'), width: 170, sortable: false, filterable: false, renderCell: (cell) => {
        return cell.row.statusChanged ? dateToUserString(cell.row.statusChanged) : '---'
      }},
    { field: 'telNumber', headerName: t('Tel. number'), width: 70, sortable: false, filterable: false, renderCell: (cell) => {
        return telNumber === cell.row.telFrom ? cell.row.telTo : cell.row.telFrom
      }},
    { field: 'content', headerName: t('Content'), flex: 1, sortable: false, filterable: false },
    { field: 'price', headerName: t('Price'), flex: 0.5, sortable: false, filterable: false, renderCell: (cell) => {
        const isCharged = cell.row.price > 0 && cell.row.deliveryStatus === MessageDeliveryStatus.delivered
        return (
            <Typography variant="span" color={isCharged ? 'success.main' : 'text.disabled'}>
              {fPrice(cell.row.price)}
            </Typography> )
      }},
  ]

  useEffect(() => {
    setForceReloadCounter(prev => prev + 1)
  }, [telNumber])

  const onSubmitSearch = () => {
    setForceReloadCounter(forceReloadCounter + 1)
  }

  return (<Page title={`${t('Search')} ${telNumber}`}>
    <Container>
      <Stack direction="row" alignItems="center" mb={1} spacing={2}>
        <Typography mb={2} variant="h4" color={'text.disabled'}>
          <Trans i18nKey={'searchingFor'} values={{ telNumber: telNumber }} />
        </Typography>
        <Box flexGrow={1} />
      </Stack>
      <Stack direction={'row'} justifyContent={'right'} mb={5} spacing={2} component={'form'} onSubmit={(e) => {
        e.preventDefault()
        onSubmitSearch()
      }}>
        <TextField
          id="datetime-local"
          label={t('From')}
          type="datetime-local"
          value={dateTimeFrom}
          onChange={(e) => {
            setDateTimeFrom(e.target.value)
          }}
          sx={{ minWidth: 250 }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          id="datetime-local"
          label={t('To')}
          type="datetime-local"
          value={dateTimeTo}
          onChange={(e) => {
            setDateTimeTo(e.target.value)
          }}
          sx={{ minWidth: 250 }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <Button type={'submit'} variant="outlined">{t('Search')}</Button>
      </Stack>
      <BaseTable
        columns={columns}
        forceReloadCounter={forceReloadCounter}
        loadCallback={async (query, page, pageSize) => {
          if (!telNumber) {
            console.log('Missing telNumber')
            return {
              data: [],
              totalCount: 0,
            }
          }
          try {
            return await (user.type === User.typeViewer ? api.viewer.message.list : api.message.list)({
              telNumber: telNumber,
              from: dateTimeFrom ? dateFromInputStringToSql(dateTimeFrom) : null,
              to: dateTimeTo ? dateFromInputStringToSql(dateTimeTo) : null,
            }, page, pageSize)
          } catch (e) {
            console.log('Failed to load messages', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }} />
    </Container>
  </Page>)
}